// This lets the app load faster on subsequent visits in production, and gives it offline capabilities.

export interface Window extends globalThis.Window {
    sw?: ServiceWorkerRegistration;
}

export interface Configuration {
    onActivated?: (registration: ServiceWorkerRegistration) => void;
    onUpdate?: (registration: ServiceWorkerRegistration) => void;
    onSuccess?: (registration: ServiceWorkerRegistration) => void;
}

const isLocalhost = Boolean(
    window.location.hostname === "localhost" ||
        // [::1] is the IPv6 localhost address.
        window.location.hostname === "[::1]" ||
        // 127.0.0.0/8 are considered localhost for IPv4.
        window.location.hostname.match(
            /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
        )
);

export const register = (config: Configuration) => {
    return new Promise<void>((resolve, reject) => {
        if (
            process.env.NODE_ENV !== "development" &&
            "serviceWorker" in navigator
        ) {
            const swUrl = "/service-worker.js";

            if (isLocalhost) {
                // This is running on localhost. Let's check if a service worker still exists or not.
                checkValidServiceWorker(swUrl, config)
                    .then(resolve)
                    .catch(reject);

                // Add some additional logging to localhost, pointing developers to the service worker/PWA documentation.
                navigator.serviceWorker.ready.then(() => {
                    console.log(
                        "This web app is being served cache-first by a service worker."
                    );
                });
            } else {
                // Is not localhost. Just register service worker
                registerValidSW(swUrl, config).then(resolve).catch(reject);
            }
        } else {
            resolve();
        }
    });
};

export const unRegister = async () => {
    if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;

        await registration.unregister();
    }
};

const checkValidServiceWorker = (
    swUrl: string | URL,
    config: Configuration | undefined
) => {
    return new Promise<void>((resolve, reject) => {
        // Check if the service worker can be found. If it can't reload the page.
        fetch(swUrl, {
            headers: { "Service-Worker": "script" },
        })
            .then((response) => {
                // Ensure service worker exists, and that we really are getting a JS file.
                const contentType = response.headers.get("content-type");
                if (
                    response.status === 404 ||
                    (contentType != null &&
                        contentType.indexOf("javascript") === -1)
                ) {
                    // No service worker found. Probably a different app. Reload the page.
                    console.error(
                        "No service worker found. Probably a different app."
                    );

                    navigator.serviceWorker.ready.then((registration) => {
                        registration.unregister().then(() => {
                            window.location.reload();
                        });
                    });

                    reject();
                } else {
                    // Service worker found. Proceed as normal.
                    registerValidSW(swUrl, config).then(resolve).catch(reject);
                }
            })
            .catch((error) => {
                console.log(
                    "No internet connection found. App is running in offline mode."
                );

                reject(error);
            });
    });
};

const registerValidSW = (
    swUrl: string | URL,
    config: Configuration | undefined
) => {
    return new Promise<void>((resolve, reject) => {
        navigator.serviceWorker
            .register(swUrl)
            .then((registration) => {
                (window as Window).sw = registration;

                let serviceWorker;

                if (registration.installing) {
                    serviceWorker = registration.installing;
                } else if (registration.waiting) {
                    serviceWorker = registration.waiting;
                } else if (registration.active) {
                    serviceWorker = registration.active;
                }

                if (serviceWorker) {
                    if (
                        serviceWorker.state === "activated" &&
                        config?.onActivated
                    ) {
                        config.onActivated(registration);
                    }

                    serviceWorker.addEventListener("statechange", function (e) {
                        const { state } = e.target as ServiceWorker;

                        if (state === "activated" && config?.onActivated) {
                            config.onActivated(registration);
                        }
                    });
                }

                registration.onupdatefound = () => {
                    const installingWorker = registration.installing;

                    if (installingWorker == null) {
                        return;
                    }

                    installingWorker.onstatechange = () => {
                        if (installingWorker.state === "installed") {
                            if (navigator.serviceWorker.controller) {
                                // At this point, the updated precached content has been fetched,
                                // but the previous service worker will still serve the older content until all client tabs are closed.
                                console.log(
                                    "New content is available and will be used when all tabs for this page are closed."
                                );

                                // Execute callback
                                if (config?.onUpdate) {
                                    config.onUpdate(registration);
                                }
                            } else {
                                // At this point, everything has been precached.
                                console.log(
                                    "Content is cached for offline use."
                                );

                                // Execute callback
                                if (config?.onSuccess) {
                                    config.onSuccess(registration);
                                }
                            }
                        }
                    };
                };

                resolve();
            })
            .catch((error) => {
                console.error(
                    "Error during service worker registration:",
                    error
                );

                reject(error);
            });
    });
};
